import 'animate.css';
import './index.less';

import React from 'react';
import { HOMEPAGE_V4_INTRO_LOGOS } from '@/shared/constants/homepage-constants';
import HomepageV4IntroCards from './Cards';
import Image from 'next/image';
import { useConfigProvider, useCreateAccountProvider } from '@/lib/context';
import { useRouter } from 'next/navigation';
import { sendEventAnalyses } from '@/lib/service';
import { ANONYMOUS_USER_ID, STATIC_FILE_URL } from '@/shared/constants';
import { EventName, EventSource } from '@/shared/constants/event';
import storage from '@/shared/utils/storage';
import { setAnonymousUser } from '@/shared/utils/utils';
import CopilotEntrypoint from './CopilotEntrypoint';
import queryString from 'query-string';
import { ArrowRightOutlined, StarsPaywallFilled } from '@pietra/icons';

const HomePageIntro = () => {
  const { isMobile } = useConfigProvider();

  const router = useRouter();
  const { showSignupDialogFromSourcing } = useCreateAccountProvider();

  const handleJoinPietra = () => {
    sendEventAnalyses({
      eventName: EventName.COMMON_VIEW,
      commonType: EventSource.HOMEPAGE,
      commonContent: 'join_pietra',
      sourceClient: isMobile ? 'mobile_web' : 'desktop_web',
      anonymousUserId: storage.get(ANONYMOUS_USER_ID) ?? setAnonymousUser(),
    });

    router.push('/sell-on-pietra/register');
  };

  const openCopilotQuery = (query: string, eventSource: string) => {
    showSignupDialogFromSourcing(
      'To use Pietra AI, please create an account first.',
      queryString.stringifyUrl({
        url: '/homebase/quiz',
        query: {
          query,
        },
      }),
      eventSource,
    );
  };

  return (
    <div className="home-page-intro-wrap">
      <div className="padded-container">
        <div className="home-page-intro-header-wrap">
          <div className="title">Meet your AI Business Partner</div>
          <div className="subtitle">
            The small business AI that finds suppliers, creates content, manages influencers, and more. Focus on what
            you love and <span className="bold">reclaim 20+ hours each week.</span>
          </div>
        </div>

        <CopilotEntrypoint
          onClickSend={(input: string) => {
            openCopilotQuery(input, 'pietra-ai-entrypoint');
          }}
          onClickCreateAccount={handleJoinPietra}
        />

        <div className="home-page-intro-copilot-explanation-wrapper">
          <div className="card">
            <Image
              className="ellipse-1"
              width={88}
              height={88}
              src={`${STATIC_FILE_URL}/a031764b413fa6d54de1e1acbeb2cccd.svg`}
              alt="data"
            />
            <Image
              className="ellipse-2"
              width={88}
              height={88}
              src={`${STATIC_FILE_URL}/9bf9fcb718b29d487515252edfbe5af8.svg`}
              alt="data"
            />
            <span className="title">Better because it knows your data</span>
            <div className="integrations">
              <div className="logo">
                <Image width={15} height={16} src="/svg/shopify.svg" alt="Shopify" />
              </div>
              <div className="logo">
                <Image width={17} height={14} src="/svg/squarespace.png" alt="SquareSpace" />
              </div>
              <div className="logo">
                <Image width={14} height={16} src="/svg/meta.svg" alt="Meta Ads" />
              </div>
              <div className="logo">
                <Image width={17} height={16} src="/svg/google.svg" alt="Google" />
              </div>
              <div className="logo">
                <Image width={17} height={18} src="/svg/instagram.svg" alt="Instagram" />
              </div>
              <div className="logo">
                <Image width={17} height={18} src="/svg/tiktok-color.svg" alt="TikTok" />
              </div>
            </div>
          </div>
          <div className="card">
            <Image
              className="ellipse-3"
              width={152}
              height={42}
              src={`${STATIC_FILE_URL}/0cb68214f20f478228b107fa56fffb4b.svg`}
              alt="data"
            />
            <span className="title">Built specifically for commerce</span>
            <div className="ecommerce">
              <StarsPaywallFilled style={{ color: '#4865FF' }} />
              <span className="text">Created 3 assets based on trending ad formats</span>
            </div>
          </div>
        </div>

        <div className="home-page-intro-rating-container">
          <div className="title">Used by the best teams</div>
          <div className="image-list-container">
            <div className="image-list">
              {HOMEPAGE_V4_INTRO_LOGOS.map((e, index) => (
                <Image
                  key={index}
                  width={isMobile ? e.width / 2 : e.width}
                  height={isMobile ? e.height / 2 : e.height}
                  src={e.src}
                  alt={e.alt}
                  className="logo"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <HomepageV4IntroCards />
    </div>
  );
};

export default HomePageIntro;
